import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Button, Loader } from '@dynamon-as/ui';
import { ComponentType, FC, ReactNode } from 'react';
import { useAuthStore } from '../stores';
import styles from './ProtectedRoute.module.css';

/**
 * Default loader component to display while checking authentication.
 * Uses @dynamon-as/ui Loader as default, but can be overridden.
 */
const DEFAULT_LOADER = (): ReactNode => <Loader isLoading />;

/**
 * Props for ProtectedRoute component
 */
export interface ProtectedRouteProps {
    children: ReactNode;
    LoaderComponent?: ComponentType;
}

/**
 * Protected route component that requires authentication.
 *
 * This component wraps its children with the withAuthenticationRequired
 * HOC from @auth0/auth0-react, ensuring that only authenticated users
 * can access the protected content.
 *
 * When a pending organization is set (from ?organization=XXXX query param),
 * the login will include the organization parameter to ensure the token
 * has the correct organization context.
 *
 * @param props - Component props including children and optional LoaderComponent
 * @returns Protected content or loading indicator
 *
 * @example
 * ```tsx
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 * ```
 *
 * @example
 * ```tsx
 * <ProtectedRoute LoaderComponent={CustomSpinner}>
 *   <AdminPanel />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
    children,
    LoaderComponent = DEFAULT_LOADER,
}) => {
    // Get pending organization from store
    const pendingOrgId = useAuthStore((state) => state.pendingOrgId);

    const { isAuthenticated, error, loginWithRedirect } = useAuth0();

    if (!isAuthenticated && !!error) {
        return (
            <div className={styles.errorContainer}>
                <p className={styles.errorText}>
                    {error instanceof Error
                        ? error.message
                        : 'Innlogging mislyktes. Vennligst prøv igjen.'}
                </p>
                <Button
                    text="Prøv igjen"
                    onClick={() => void loginWithRedirect()}
                />
            </div>
        );
    }
    // Create the protected component using Auth0's HOC
    const Component = withAuthenticationRequired(() => children, {
        onRedirecting: () => <LoaderComponent />,
        ...(pendingOrgId && {
            loginOptions: {
                authorizationParams: {
                    organization: pendingOrgId,
                },
            },
        }),
    });

    return <Component />;
};

export default ProtectedRoute;
